import { forwardRef, Fragment, useEffect, useImperativeHandle } from 'react';

import { AccessCodePickerView } from 'pages/custom_tests_v1/components/assign_wizard/components/access_code_picker/AccessCodePicker';
import { ClassroomPickerView } from 'pages/custom_tests_v1/components/assign_wizard/components/classroom_picker/ClassroomPicker';
import { SelectionStep } from 'pages/custom_tests_v1/components/assign_wizard/steps/SelectionStep';
import { AssignWizardContext } from 'pages/custom_tests_v1/components/assign_wizard/context';
import { ReviewStep } from 'pages/custom_tests_v1/components/assign_wizard/steps/ReviewStep';
import { addContentToTransactionChunked } from 'utils/addContentToTransaction';
import { SelectedContentReference, TransactionApi } from 'apis/TransactionApi';
import { GMetrixStepper } from 'components/GMetrixStepper/GMetrixStepper';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { filterOutNonExistentOrderIds, toDayString } from 'utils';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { useNotification } from 'app/notifications';
import { CustomTestApi } from 'apis/CustomTestApi';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';
import { useApp } from 'app';

export type AssignableItem = {
    CreatedDate: string;
    Description?: string;
    Id: number;
    Identifier?: string;
    Name?: string;
    UsesRemaining?: number;
};

export type AssignWizardShowOptions = {
    testId: number;
    testName: string;
};

export type AssignWizardContextValue = {
    accessCodes: AssignableItem[];
    classrooms: AssignableItem[];
    initialSelectedAccessCodes: AssignableItem[];
    initialSelectedClassrooms: AssignableItem[];
    loading: boolean;
    openAccessCodePicker: () => void;
    openClassroomPicker: () => void;
    selectedAccessCodes: AssignableItem[];
    selectedClassrooms: AssignableItem[];
    setSelectedAccessCodes: (accessCodes: AssignableItem[]) => void;
    setSelectedClassrooms: (accessCodes: AssignableItem[]) => void;
    testId: number;
    testName: string;
}

export type AssignWizardHandle = {
    hide: () => void;
    show: (options: AssignWizardShowOptions) => Promise<boolean>;
    visible: boolean;
};

enum EStep {
    SELECTION,
    REVIEW
}

export const AssignWizard = forwardRef<AssignWizardHandle, any>((_, forwardedRef) => {

    const [state, setState, resetState] = useSuperState({
        accessCodes: [] as AssignableItem[],
        classrooms: [] as AssignableItem[],
        draftSelected: [] as AssignableItem[],
        error: null as string,
        initialSelectedAccessCodes: [] as AssignableItem[],
        initialSelectedClassrooms: [] as AssignableItem[],
        loading: true,
        reject: Function(),
        resolve: Function(),
        selectedAccessCodes: [] as AssignableItem[],
        selectedClassrooms: [] as AssignableItem[],
        step: EStep.SELECTION as EStep,
        submitting: false,
        subView: null as null | 'accessCodes' | 'classrooms',
        testId: 0,
        testName: '',
        validationError: '',
        visible: false
    });

    const { accessCodes, classrooms, initialSelectedAccessCodes, initialSelectedClassrooms, loading, resolve, selectedAccessCodes,
        selectedClassrooms, testId, testName, visible } = state;

    const { selectedPartner } = useApp();
    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    const stepItems = [
        { label: formatMessage({ id: 'select_assignments.382b506c', defaultMessage: 'Select Assignments' }) },
        { label: formatMessage({ id: 'review_and_assign.f9bf4263', defaultMessage: 'Review & Assign' }) }
    ];

    const setSelectedClassrooms = (selectedClassrooms: AssignableItem[]) => setState({ selectedClassrooms });

    const setSelectedAccessCodes = (selectedAccessCodes: AssignableItem[]) => setState({ selectedAccessCodes });

    const openAccessCodePicker = () => setState({ draftSelected: state.selectedAccessCodes, subView: 'accessCodes' });

    const openClassroomPicker = () => setState({ draftSelected: state.selectedClassrooms, subView: 'classrooms' });

    const setDraftSelected = (draftSelected: AssignableItem[]) => setState({ draftSelected });

    const cancelPicker = () => setState({ draftSelected: [], subView: null });

    const confirmPicker = () => setState(state.subView === 'accessCodes'
        ? { draftSelected: [], selectedAccessCodes: state.draftSelected, subView: null }
        : { draftSelected: [], selectedClassrooms: state.draftSelected, subView: null });

    const show = (options: AssignWizardShowOptions) => new Promise<boolean>((resolve, reject) => {

        const { testId, testName } = options;

        setState({
            reject,
            resolve,
            testId,
            testName,
            visible: true
        });

    });

    const hide = () => {

        resolve(false);
        resetState();

    };

    const handleClose = () => {

        resolve(false);
        resetState();

    };

    const validateCurrentStep = (): boolean => {

        if (state.step === 0 && state.selectedAccessCodes.length === 0 && state.selectedClassrooms.length === 0) {

            setState({ validationError: formatMessage({ id: 'please_select_at_least_one_access_code_o.646b9f03', defaultMessage: 'Please select at least one access code or classroom.' }) });
            return false;

        }

        setState({ validationError: '' });
        return true;

    };

    const handleNext = () => {

        if (!validateCurrentStep()) return;

        setState({
            step: EStep.REVIEW,
            validationError: ''
        });

    };

    const handleBack = () => setState({
        step: EStep.SELECTION,
        validationError: ''
    });

    // Resolves the custom test's content reference (id + the license order it should consume).
    // A custom test's content deliverable Id equals its TestId, so match on Id + IsCustomTest.
    const buildTestContentRef = async (): Promise<SelectedContentReference> => {

        const [content, licenseData] = await Promise.all([
            TransactionApi.GetAllContentForTransactions(false, true, selectedPartner.id),
            TransactionApi.GetAllLicenses(selectedPartner.id)
        ]);

        const testContent = content.contentFlatList.find(c => c.IsCustomTest && c.Id === state.testId);
        const orderIds = testContent?.OrderIds ?? [];
        const restrictedOrderId = filterOutNonExistentOrderIds(orderIds, licenseData.licenses)[0] ?? orderIds[0] ?? 0;

        return { id: state.testId, restrictedOrderId };

    };

    // Adds the custom test as real content to every NEWLY assigned access code / classroom so
    // students can start it. Already-assigned items are skipped (they already have the content).
    // Returns the display names of any items whose content add failed.
    const assignTestContentToNewItems = async (): Promise<string[]> => {

        const newAccessCodes = state.selectedAccessCodes.filter(
            ac => !state.initialSelectedAccessCodes.some(({ Id }) => Id === ac.Id)
        );
        const newClassrooms = state.selectedClassrooms.filter(
            c => !state.initialSelectedClassrooms.some(({ Id }) => Id === c.Id)
        );

        if (newAccessCodes.length === 0 && newClassrooms.length === 0) return [];

        let testContentRef: SelectedContentReference;

        try {

            testContentRef = await buildTestContentRef();

        } catch (e) {

            return [
                ...newAccessCodes.map(ac => ac.Identifier ?? ac.Name ?? String(ac.Id)),
                ...newClassrooms.map(c => c.Name ?? String(c.Id))
            ];

        }

        const failed: string[] = [];

        for (const accessCode of newAccessCodes) {

            try {

                await addContentToTransactionChunked(accessCode.Id, [testContentRef], [], []);

            } catch (e) {

                failed.push(accessCode.Identifier ?? accessCode.Name ?? String(accessCode.Id));

            }

        }

        for (const classroom of newClassrooms) {

            try {

                const transactions = await StudentGroupApi.GetTransactionsFromGroup(classroom.Id, selectedPartner.id);
                const transactionId = transactions?.[0]?.Id;

                if (!transactionId) {

                    failed.push(classroom.Name ?? String(classroom.Id));
                    continue;

                }

                await addContentToTransactionChunked(transactionId, [testContentRef], [], []);

            } catch (e) {

                failed.push(classroom.Name ?? String(classroom.Id));

            }

        }

        return failed;

    };

    const handleSubmit = async () => {

        try {

            setState({ submitting: true });

            const transactionIds = state.selectedAccessCodes.map(t => t.Id);
            const groupIds = state.selectedClassrooms.map(c => c.Id);
            const result = await CustomTestApi.SaveAssignedTransactionsAndGroups(state.testId, selectedPartner.id, transactionIds, groupIds);

            if (!result?.Done) {

                createToast(
                    result?.Message || formatMessage({ id: 'failed_to_save_assignments.3fd63746', defaultMessage: 'Failed to save assignments.' }),
                    'danger'
                );
                setState({ submitting: false });
                return;

            }

            const failedContent = await assignTestContentToNewItems();

            if (failedContent.length > 0) {

                createToast(
                    formatMessage({ id: 'assignments_saved_but_the_test_could_not.b15cf146', defaultMessage: 'Assignments saved, but the test could not be added as content to: ' }) + failedContent.join(', '),
                    'warning'
                );

            }

            resolve(true);
            resetState();

        } catch (e: any) {

            // ApiCaller rethrows the serialized .NET exception, whose text is on `Message`.
            createToast(
                e?.Message || e?.message || formatMessage({ id: 'failed_to_save_assignments.3fd63746', defaultMessage: 'Failed to save assignments.' }),
                'danger'
            );
            setState({ submitting: false });

        }

    };

    const handleActionClick = () => state.step === EStep.SELECTION ? handleNext() : handleSubmit();

    const getActionButtonText = (): string => {

        switch (state.step){

            case EStep.SELECTION:
                return formatMessage({ id: 'next.50b7834e', defaultMessage: 'Next' });
            case EStep.REVIEW:
                return state.submitting
                    ? (formatMessage({ id: 'assigning.8cc92c66', defaultMessage: 'Assigning...' }))
                    : (formatMessage({ id: 'assign.0dabb6f2', defaultMessage: 'Assign' }));
            default:
                return '';

        }

    };

    const fetchData = async () => {

        try {

            const [transactions, groups, assignedInfo] = await Promise.all([
                TransactionApi.GetAll(),
                StudentGroupApi.GetAll('Partner'),
                CustomTestApi.GetAssignedTransactionsAndGroups(testId, selectedPartner.id)
            ]);

            // Store the created date as a sortable YYYY-MM-DD day string. CreatedDateString is a
            // locale ToShortDateString ("9/3/2026") that sorts wrong as text; toDayString of the
            // real date gives a lexicographically-chronological value the grid can sort correctly.
            const accessCodes: AssignableItem[] = transactions.map(t => ({
                CreatedDate: toDayString(t.CreatedDate),
                Id: t.Id,
                Identifier: t.Name,
                UsesRemaining: t.UsesRemaining ?? 0
            }));

            const classrooms: AssignableItem[] = groups.map(g => ({
                CreatedDate: toDayString(g.DateCreate),
                Description: g.Description ?? '',
                Id: g.Id,
                Name: g.Name
            }));

            const initialSelectedAccessCodes = accessCodes.filter(({ Id }) => assignedInfo.AssignedTransactionIds.includes(Id));
            const initialSelectedClassrooms = classrooms.filter(({ Id }) => assignedInfo.AssignedStudentGroupIds.includes(Id));

            setState({
                accessCodes,
                classrooms,
                initialSelectedAccessCodes,
                initialSelectedClassrooms,
                loading: false,
                selectedAccessCodes: initialSelectedAccessCodes,
                selectedClassrooms: initialSelectedClassrooms
            });

        } catch (e){

            setState({
                error: e?.message || e,
                loading: false
            });

        }

    };

    const handleChangeStep = e => !state.subView && e.value <= state.step && setState({ step: e.value, validationError: '' });

    useImperativeHandle(forwardedRef, () => ({
        hide,
        show,
        visible
    }));

    // fetchData is intentionally omitted from deps — runs once on mount
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {

        if (!visible) return;

        fetchData();

    }, [visible]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const context: AssignWizardContextValue = {
        accessCodes,
        classrooms,
        initialSelectedAccessCodes,
        initialSelectedClassrooms,
        loading,
        openAccessCodePicker,
        openClassroomPicker,
        selectedAccessCodes,
        selectedClassrooms,
        setSelectedAccessCodes,
        setSelectedClassrooms,
        testId,
        testName
    };

    const inPicker = state.subView !== null;

    const Steps = [SelectionStep, ReviewStep];
    const StepComponent = Steps?.[state.step] || Fragment;

    return (
        <AssignWizardContext.Provider value={context}>
            <GMetrixDialog
                closeButtonText={inPicker
                    ? (formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' }))
                    : (state.step === 0 ? (formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' })) : (formatMessage({ id: 'back.b6238c9e', defaultMessage: 'Back' })))}
                actionButtonClick={inPicker ? confirmPicker : handleActionClick}
                actionButtonDisabled={state.submitting}
                actionButtonText={inPicker ? (formatMessage({ id: 'add.e3ee51bb', defaultMessage: 'Add' })) : getActionButtonText()}
                dialogLabel={formatMessage({ id: 'assign_test.5de97fef', defaultMessage: 'Assign Test' })}
                onCancel={inPicker ? cancelPicker : (state.step === 0 ? handleClose : handleBack)}
                onClose={inPicker ? cancelPicker : handleClose}
                show={state.visible}
                size="xl"
            >
                <div className="my-4">
                    <GMetrixStepper
                        handleChange={handleChangeStep}
                        linear={false}
                        setStep={(step: EStep) => setState({ step })}
                        step={state.step}
                        stepItems={stepItems}
                    />
                </div>

                {!inPicker && state.validationError && (
                <div className="alert alert-danger">
                    {state.validationError}
                </div>
                )}

                {inPicker ?
                    (
                        <>
                            <h6 className="mb-3">
                                {state.subView === 'accessCodes'
                                    ? (formatMessage({ id: 'select_access_codes.11a08246', defaultMessage: 'Select Access Codes' }))
                                    : (formatMessage({ id: 'select_classrooms.f1589a6f', defaultMessage: 'Select Classrooms' }))}
                            </h6>
                            {state.subView === 'accessCodes' ?
                                (
                                    <AccessCodePickerView
                                        accessCodes={accessCodes}
                                        assignedIds={initialSelectedAccessCodes.map(({ Id }) => Id)}
                                        onChange={setDraftSelected}
                                        selected={state.draftSelected}
                                    />
                                )
                                :
                                (
                                    <ClassroomPickerView
                                        assignedIds={initialSelectedClassrooms.map(({ Id }) => Id)}
                                        classrooms={classrooms}
                                        onChange={setDraftSelected}
                                        selected={state.draftSelected}
                                    />
                                )
                            }
                        </>
                    )
                    :
                    (
                        <StepComponent />
                    )
                }

            </GMetrixDialog>

        </AssignWizardContext.Provider>
    );

});

AssignWizard.displayName = 'AssignWizard';
